import { useMemo } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { statsApi, type StatKind, type StatResult } from '../../api/stats.js';
import { LineChart, type Series } from '../../components/LineChart.js';
import { Spinner, ErrorBox, EmptyBox, Field } from '../../components/ui.js';
import { useUrlUpdate } from '../../lib/hooks.js';
import { money, num } from '../../lib/format.js';

/**
 * 주문 통계.
 *
 * 원본: lottery/order/lottery_order_stat_kr.php, randombox/order/randombox_order_stat.php
 * 일별 추이 그래프 + 월별 피벗 표. 월이 컬럼으로 늘어나는 2단 헤더라
 * 리포트 정의 틀에는 안 들어가서 화면을 따로 만들었다.
 */

const TITLES: Record<StatKind, string> = {
  lottery: 'F주문통계',
  randombox: 'N주문통계',
};

/** 원본 기본 구간과 같게 올해 1월 1일부터 오늘까지. */
function defaultRange() {
  const now = new Date();
  const to = now.toISOString().slice(0, 10);
  const from = `${now.getFullYear()}-01-01`;
  return { from, to };
}

export function StatPage({ kind }: { kind: StatKind }) {
  const [params, setParams] = useSearchParams();
  const fallback = defaultRange();
  const from = params.get('from') ?? fallback.from;
  const to = params.get('to') ?? fallback.to;

  const stats = useQuery({
    queryKey: ['stats', kind, from, to],
    queryFn: () => statsApi.load(kind, from, to),
  });

  const update = useUrlUpdate(params, setParams);

  return (
    <section>
      <header className="page-head">
        <div>
          <h1 className="page-title">{TITLES[kind]}</h1>
          <p className="page-desc">일별 판매 추이와 월별 집계.</p>
        </div>
        <div className="page-head__actions">
          {stats.isFetching && <span className="page-head__busy">갱신 중…</span>}
        </div>
      </header>

      <div className="filters">
        <Field label="시작일">
          <input
            className="input"
            type="date"
            value={from}
            max={to}
            onChange={(e) => update({ from: e.target.value })}
          />
        </Field>
        <Field label="끝일">
          <input
            className="input"
            type="date"
            value={to}
            min={from}
            onChange={(e) => update({ to: e.target.value })}
          />
        </Field>
      </div>

      {stats.isPending ? (
        <Spinner />
      ) : stats.isError ? (
        <ErrorBox error={stats.error} onRetry={() => void stats.refetch()} />
      ) : stats.data.monthly.length === 0 ? (
        <EmptyBox>이 구간에는 주문이 없습니다.</EmptyBox>
      ) : (
        <>
          <DailyChart data={stats.data} />
          <MonthlyTable data={stats.data} />
        </>
      )}
    </section>
  );
}

function DailyChart({ data }: { data: StatResult }) {
  const { labels, series } = useMemo(() => {
    const labels = [...new Set(data.daily.map((d) => d.day))].sort();
    const nameById = new Map(data.monthly.map((m) => [m.id, m.name ?? m.id]));
    const byId = new Map<string, { x: string; y: number }[]>();
    for (const d of data.daily) {
      const list = byId.get(d.id) ?? [];
      list.push({ x: d.day, y: d.tickets });
      byId.set(d.id, list);
    }
    const series: Series[] = [...byId.entries()]
      .map(([id, points]) => ({ id, label: nameById.get(id) ?? id, points }))
      // 많이 팔린 순으로 두어 범례 순서가 매번 바뀌지 않게 한다.
      .sort((a, b) => sum(b.points) - sum(a.points));
    return { labels, series };
  }, [data]);

  return (
    <div className="panel">
      <h2 className="panel__title">일별 판매매수</h2>
      <LineChart labels={labels} series={series} />
    </div>
  );
}

function sum(points: { y: number }[]) {
  return points.reduce((n, p) => n + p.y, 0);
}

/**
 * 월별 피벗 표. 원본과 같이 행=랜덤박스, 열=월×(판매매수·판매금액) 이고
 * 맨 앞에 합계 열이 온다.
 */
function MonthlyTable({ data }: { data: StatResult }) {
  const rows = useMemo(() => {
    const byId = new Map<
      string,
      { id: string; name: string; saleEnd: string | null; cells: Map<string, { tickets: number; sales: number }> }
    >();
    for (const m of data.monthly) {
      const row = byId.get(m.id) ?? {
        id: m.id,
        name: m.name ?? m.id,
        saleEnd: m.saleEnd,
        cells: new Map(),
      };
      row.cells.set(m.ym, { tickets: m.tickets, sales: m.sales });
      if (!row.saleEnd && m.saleEnd) row.saleEnd = m.saleEnd;
      byId.set(m.id, row);
    }

    // 원본 정렬: 판매종료일 순, 종료일 없는 건(판매중) 뒤로, 같으면 이름 순.
    return [...byId.values()].sort((a, b) => {
      if (a.saleEnd === b.saleEnd) return a.name.localeCompare(b.name);
      if (!a.saleEnd) return 1;
      if (!b.saleEnd) return -1;
      return a.saleEnd.localeCompare(b.saleEnd);
    });
  }, [data]);

  const totalOf = (r: (typeof rows)[number]) => {
    let tickets = 0;
    let sales = 0;
    for (const c of r.cells.values()) {
      tickets += c.tickets;
      sales += c.sales;
    }
    return { tickets, sales };
  };

  const grand = rows.reduce(
    (acc, r) => {
      const t = totalOf(r);
      acc.tickets += t.tickets;
      acc.sales += t.sales;
      return acc;
    },
    { tickets: 0, sales: 0 },
  );

  const monthTotals = data.months.map((ym) =>
    rows.reduce(
      (acc, r) => {
        const c = r.cells.get(ym);
        acc.tickets += c?.tickets ?? 0;
        acc.sales += c?.sales ?? 0;
        return acc;
      },
      { tickets: 0, sales: 0 },
    ),
  );

  return (
    <div className="panel">
      <h2 className="panel__title">월별 집계</h2>
      <div className="table-wrap">
        <table className="table table--pivot">
          <thead>
            <tr>
              <th rowSpan={2}>랜덤박스</th>
              <th rowSpan={2}>판매종료</th>
              <th colSpan={2} className="pivot-total">합계</th>
              {data.months.map((m) => (
                <th key={m} colSpan={2}>
                  {m}
                </th>
              ))}
            </tr>
            <tr>
              <th className="pivot-total">판매매수</th>
              <th className="pivot-total">판매금액</th>
              {data.months.map((m) => [
                <th key={`${m}-t`}>판매매수</th>,
                <th key={`${m}-s`}>판매금액</th>,
              ])}
            </tr>
          </thead>
          <tbody>
            {rows.map((r) => {
              const t = totalOf(r);
              return (
                <tr key={r.id}>
                  <td>{r.name}</td>
                  <td className="td-muted">{r.saleEnd ?? '판매중'}</td>
                  <td className="pivot-total num">{num(t.tickets)}</td>
                  <td className="pivot-total num">{money(t.sales)}</td>
                  {data.months.map((m) => {
                    const c = r.cells.get(m);
                    return [
                      <td key={`${m}-t`} className="num">
                        {c ? num(c.tickets) : <span className="td-muted">-</span>}
                      </td>,
                      <td key={`${m}-s`} className="num">
                        {c ? money(c.sales) : <span className="td-muted">-</span>}
                      </td>,
                    ];
                  })}
                </tr>
              );
            })}
          </tbody>
          <tfoot>
            <tr>
              <td colSpan={2}>합계</td>
              <td className="pivot-total num">{num(grand.tickets)}</td>
              <td className="pivot-total num">{money(grand.sales)}</td>
              {monthTotals.map((t, i) => [
                <td key={`${data.months[i]}-t`} className="num">
                  {num(t.tickets)}
                </td>,
                <td key={`${data.months[i]}-s`} className="num">
                  {money(t.sales)}
                </td>,
              ])}
            </tr>
          </tfoot>
        </table>
      </div>
    </div>
  );
}
